import dayjs from 'dayjs';

import type { ClassModuleDetailViewModel } from '@/shared/api/generated';

import type {
  ClassModuleDisciplineForm,
  ClassModuleFormValues,
  ClassModuleLessonView,
  ClassModuleRecurrenceForm,
  LinkedClassForm,
} from '../types/classModuleForm';

/**
 * Converte uma data-hora ISO do backend no formato ISO de data (`YYYY-MM-DD`)
 * do DatePicker. Usa a data de calendário do ISO (fatia `YYYY-MM-DD`), sem
 * conversão de fuso, para evitar deslocamento de dia.
 */
function toDateInput(value?: string | null): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}

/** Extrai `HH:mm` de um horário (aceita `HH:mm`, `HH:mm:ss` ou data-hora ISO). */
function toTimeInput(value?: string | null): string {
  if (!value) return '';
  if (/^\d{2}:\d{2}/.test(value)) return value.slice(0, 5);
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('HH:mm') : '';
}

/**
 * Adapta o `ClassModuleDetailViewModel` (leitura) para os valores editáveis do
 * formulário, preservando rótulos de exibição (nome da turma, disciplina,
 * professor, sala) e as aulas geradas pelo backend (somente leitura).
 *
 * @param detail - Detalhe do módulo retornado por `GET /class-modules/{id}`.
 * @returns Valores iniciais do formulário de edição.
 */
export function adaptDetailToForm(detail: ClassModuleDetailViewModel): ClassModuleFormValues {
  const linkedClasses: LinkedClassForm[] = (detail.classStudents ?? []).map(cs => ({
    classStudentId: cs.id ?? 0,
    name: cs.name ?? '',
    course: '',
  }));

  const disciplines: ClassModuleDisciplineForm[] = (detail.disciplines ?? []).map(d => {
    const recurrences: ClassModuleRecurrenceForm[] = (d.recurrences ?? []).map(r => ({
      type: r.type ?? 1,
      interval: r.interval ?? 1,
      weekDay: r.weekDay ?? null,
      monthDay: r.monthDay ?? null,
      startTime: toTimeInput(r.startTime),
      endTime: toTimeInput(r.endTime),
    }));

    const lessons: ClassModuleLessonView[] = (d.classes ?? []).map(c => ({
      ...(c.id !== undefined ? { id: c.id } : {}),
      ...(c.startDate !== undefined ? { startDate: c.startDate } : {}),
      ...(c.endDate !== undefined ? { endDate: c.endDate } : {}),
      ...(c.status !== undefined ? { status: c.status } : {}),
    }));

    return {
      disciplineId: d.disciplineId ?? 0,
      disciplineName: d.disciplineName ?? '',
      classroomId: d.classroomId ?? null,
      classroomName: d.classroomName ?? '',
      teacherPersonId: d.teacherPersonId ?? null,
      teacherName: d.teacherName ?? '',
      quantity: d.quantity ?? 0,
      type: d.type ?? 1,
      planningWithExam: d.planningWithExam ?? false,
      useMoveQA: d.useMoveQA ?? false,
      isVisualizationAttendance: d.isVisualizationAttendance ?? false,
      recurrences,
      lessons,
    };
  });

  return {
    companyId: detail.companyId ?? null,
    modality: detail.modality ?? null,
    description: detail.description ?? '',
    dateStart: toDateInput(detail.dateStart),
    dateFinish: toDateInput(detail.dateFinish),
    linkedClasses,
    disciplines,
  };
}
